import { FC, useMemo, useState } from "react";
import { cn, Icon, Panel, TreeNode, TreeView, Typography } from "@vintecc/components";
import { useAppStore } from "@/store/appStore";
import { CompanyWithSubcompanies, transformCompanyListToTree } from "@/helpers/companyList";
import { PanelSearchHeader } from "@/components/panels/PanelHeader/PanelSearchHeader";
import { useQuery } from "@tanstack/react-query";
import { queries } from "@/modules/identity-and-access/companies/services/requests";
import { FullScreenLoader } from "@/components/Loader/FullscreenLoader";
import { useCommonTranslation } from "@/services/culture/common.translations";

import { faArrowRight, faBuilding } from "@vintecc/pro-light-svg-icons";

interface CompanySelectPanelProps {
  onClose?: () => void;
  onSelect: (companyName: string) => void;
  panelId?: string;
}

export const CompanySelectPanel: FC<CompanySelectPanelProps> = ({ onClose, onSelect, panelId }) => {
  const { data: companyList = [], isLoading } = useQuery(queries.getAllCompaniesNameId);
  const currentCompany = useAppStore((state) => state.currentCompany);
  const { t: commonT } = useCommonTranslation();
  const [search, setSearch] = useState("");
  const companiesWithSubcompanies = useMemo(() => transformCompanyListToTree(companyList, search), [companyList, search]);
  const companyNameById = useMemo(() => {
    const map = new Map<string, string>();

    const stack = [...companiesWithSubcompanies];

    while (stack.length > 0) {
      const company = stack.pop();

      if (!company) {
        continue;
      }

      if (company.name) {
        map.set(company.id!.toString(), company.name);
      }

      if (company.subcompanies && company.subcompanies.length > 0) {
        stack.push(...company.subcompanies);
      }
    }

    return map;
  }, [companiesWithSubcompanies]);

  const handleClick = (name?: string | null) => {
    if (name && currentCompany !== name) onSelect(name);
  };

  const companyToTreeNode = ({ id, name, subcompanies }: CompanyWithSubcompanies): TreeNode => ({
    id: id!.toString(),
    label: (
      <Typography
        variant="small-p"
        className={cn({ "text-(--theme-default-color) font-bold": name === currentCompany })}
        aria-current={name === currentCompany ? "true" : undefined}
      >
        {name || commonT("common.noName")}
      </Typography>
    ),
    startDecorator: <Icon icon={faBuilding} className={cn({ "text-(--theme-default-color)": name === currentCompany })} />,
    endDecorator: name === currentCompany ? <Icon icon={faArrowRight} className="text-(--theme-default-color)" /> : null,
    children: subcompanies && subcompanies.length > 0 ? subcompanies.map(companyToTreeNode) : undefined,
  });

  const companyTreeItems: TreeNode[] = useMemo(
    () => companiesWithSubcompanies?.map((parent) => companyToTreeNode(parent)) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companiesWithSubcompanies, currentCompany],
  );

  return (
    <Panel testId={panelId} id={panelId} role="dialog" className="flex flex-col" aria-label={commonT("common.selectCompany")}>
      <PanelSearchHeader
        title={commonT("common.selectCompany")}
        icon={<Icon icon={faBuilding} className="text-grey-400" size="lg" />}
        onClose={onClose}
        search={search}
        setSearch={setSearch}
      />
      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <div className="p-8 overflow-auto" role="region" aria-label={commonT("common.company")} data-current-company={currentCompany}>
          <TreeView
            defaultExpandedState={{ type: "all" }}
            items={companyTreeItems}
            onAction={(nodeId) => handleClick(companyNameById.get(nodeId))}
            testId="TreeView"
          />
        </div>
      )}
    </Panel>
  );
};
